import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { TEAMS_ENABLED } from '../config/features'
import { useTranslation } from 'react-i18next'
import { useStore } from '../store/useStore'
import { Plus, Check, X, ChevronDown, ChevronUp, GripVertical, Users, Eye, EyeOff, Pencil } from 'lucide-react'
import ConfirmDialog from './ConfirmDialog'
import ProjectSettingsModal, { type Milestone } from './ProjectSettingsModal'
import MilestoneTemplateModal, { type MilestoneTemplate } from './MilestoneTemplateModal'

import { Tooltip } from './Tooltip'
import { ShareProjectModal } from './team/ShareProjectModal'
import { useAuth } from '../contexts/AuthContext'
import { getUserColorFromId, getUserInitial } from '../utils/userColor'
import { getTeamMembersWithProfiles } from '../services/teamService'
import {
  getMilestonesByProjectId,
  getMilestoneTemplatesByTeamId,
  getMilestoneTemplatesByUserId,
  createProjectMilestone,
  updateProjectMilestone,
  deleteProjectMilestone,
  createMilestoneTemplate,
  createPersonalMilestoneTemplate,
  updateMilestoneTemplate,
  deleteMilestoneTemplate,
  initializePersonalTemplatesIfNeeded,
  initializeTeamTemplatesIfNeeded,
  type ProjectMilestone,
  type MilestoneTemplate as DBMilestoneTemplate,
} from '../services/milestoneService'

// DB型からフロントエンド型への変換
const convertDBMilestoneToLocal = (dbMilestone: ProjectMilestone): Milestone => ({
  id: dbMilestone.id,
  projectId: dbMilestone.project_id,
  templateId: dbMilestone.template_id,
  title: dbMilestone.title,
  startDate: dbMilestone.start_date,
  endDate: dbMilestone.end_date,
  iconType: dbMilestone.icon_type,
  iconValue: dbMilestone.icon_value,
  color: dbMilestone.color,
  sortOrder: dbMilestone.sort_order,
})

const convertDBTemplateToLocal = (dbTemplate: DBMilestoneTemplate): MilestoneTemplate => ({
  id: dbTemplate.id,
  teamId: dbTemplate.team_id || '',  // 個人テンプレートの場合は空文字
  title: dbTemplate.title,
  iconType: dbTemplate.icon_type,
  iconValue: dbTemplate.icon_value,
  color: dbTemplate.color,
  sortOrder: dbTemplate.sort_order,
})
import {
  DndContext,
  pointerWithin,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// チームメンバーリスト表示コンポーネント（ポータルで表示）
interface TeamMembersPopoverProps {
  teamId: string
  memberCount: number
  onClose: () => void
  anchorRect: DOMRect | null
}

function TeamMembersPopover({ teamId, memberCount, onClose, anchorRect }: TeamMembersPopoverProps) {
  const [members, setMembers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const popoverRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const loadMembers = async () => {
      try {
        // RPC関数でチームメンバー一覧を取得（プロファイル付き）
        const membersData = await getTeamMembersWithProfiles(teamId)

        // データ形式を既存のコードに合わせて変換
        const membersWithProfiles = membersData.map(member => ({
          id: member.id,
          role: member.role,
          joined_at: member.joined_at,
          user_id: member.user_id,
          user_profiles: {
            id: member.user_id,
            nickname: member.nickname,
            email: member.email
          }
        }))

        setMembers(membersWithProfiles)
      } catch (error) {
        console.error('Error loading team members:', error)
      } finally {
        setLoading(false)
      }
    }

    loadMembers()
  }, [teamId])

  // 外側クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner': return '👑'
      case 'admin': return '🛡️'
      default: return '👤'
    }
  }

  const getRoleName = (role: string) => {
    switch (role) {
      case 'owner': return 'オーナー'
      case 'admin': return '管理者'
      default: return 'メンバー'
    }
  }

  if (!anchorRect) return null

  // ポータルでbody直下にレンダリング
  return createPortal(
    <div
      ref={popoverRef}
      className="fixed bg-white rounded-lg shadow-lg border border-gray-200 p-3 z-[9999] min-w-[200px]"
      style={{
        top: anchorRect.bottom + 4,
        left: anchorRect.right - 200, // 右端揃え
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-xs font-semibold text-gray-700 mb-2">
        チームメンバー ({memberCount}名)
      </div>
      {loading ? (
        <div className="text-xs text-gray-500 py-2">読み込み中...</div>
      ) : (
        <div className="space-y-1.5">
          {members.map((member) => {
            const userProfile = member.user_profiles
            const colorClasses = userProfile ? getUserColorFromId(userProfile.id) : 'from-gray-400 to-gray-600'
            const initial = userProfile ? getUserInitial(userProfile.nickname, userProfile.email) : '??'

            return (
              <div key={member.id} className="flex items-center gap-2 text-xs">
                {/* ユーザーアイコン */}
                <div
                  className={`h-6 w-6 rounded-full bg-gradient-to-br ${colorClasses} flex items-center justify-center flex-shrink-0`}
                  title={userProfile?.nickname || userProfile?.email || '不明'}
                >
                  <span className="text-white font-semibold text-[10px]">
                    {initial}
                  </span>
                </div>
                {/* 権限アイコン */}
                <span className="text-sm">{getRoleIcon(member.role)}</span>
                {/* ユーザー情報 */}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {userProfile?.nickname || userProfile?.email || '不明'}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    {getRoleName(member.role)}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>,
    document.body
  )
}

interface SortableProjectItemProps {
  project: any
  isSelected: boolean
  isEditing: boolean
  editProjectName: string
  onSelect: () => void
  onCancelEdit: () => void
  onSaveEdit: () => void
  onOpenSettings: () => void
  setEditProjectName: (name: string) => void
  updateProject: (id: string, updates: any) => Promise<void>
  deleteProject: (id: string) => Promise<void>
  isTemporary?: boolean
}

function SortableProjectItem({
  project,
  isSelected,
  isEditing,
  editProjectName,
  onSelect,
  onCancelEdit,
  onSaveEdit,
  onOpenSettings,
  setEditProjectName,
  updateProject,
  deleteProject,
  isTemporary = false
}: SortableProjectItemProps) {
  const { user } = useAuth()
  const [showMembersList, setShowMembersList] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [membersButtonRect, setMembersButtonRect] = useState<DOMRect | null>(null)
  const membersButtonRef = useRef<HTMLSpanElement>(null)

  // プロジェクトのオーナーかどうかを判定
  const isProjectOwner = user?.id === project.user_id

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: project.id,
    disabled: isTemporary  // 一時保存はドラッグ無効
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`mt-0.5 sm:mt-1 rounded-lg transition-colors border border-gray-400 ${
        isSelected ? 'bg-indigo-50' : 'hover:bg-gray-50'
      }`}
    >
      {isEditing ? (
        <div className="flex items-center gap-1 sm:gap-2 px-2 py-1 sm:px-3 sm:py-2">
          <input
            type="text"
            value={editProjectName}
            onChange={(e) => setEditProjectName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && onSaveEdit()}
            className="flex-1 px-2 py-1 text-xs sm:text-sm border rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            autoFocus
          />
          <Tooltip content="保存">
            <button
              onClick={onSaveEdit}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
            >
              <Check className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </Tooltip>
          <Tooltip content="キャンセル">
            <button
              onClick={onCancelEdit}
              className="p-1 text-red-600 hover:bg-red-50 rounded"
            >
              <X className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          </Tooltip>
        </div>
      ) : (
        <div className="flex flex-col relative">
          {/* ドラッグハンドル（絶対位置で縦中央配置） */}
          {!isTemporary && (
            <div
              {...attributes}
              {...listeners}
              className="absolute left-0 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-700 touch-none z-10"
            >
              <GripVertical className="h-4 w-4 sm:h-4 sm:w-4" />
            </div>
          )}

          {/* 1行目: プロジェクト名 */}
          <div className={`flex items-center ${project.team ? '-mt-1' : 'mt-0.5'}`}>
            {/* ドラッグハンドル分のスペース確保 */}
            <div className="w-8 sm:w-10 flex-shrink-0">
              {/* スペース確保のみ */}
            </div>
            <button
              onClick={onSelect}
              className={`flex-1 px-2 py-1 sm:px-3 sm:py-1.5 text-left min-w-0 ${isProjectOwner ? 'pr-32 sm:pr-40' : 'pr-12 sm:pr-16'}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mr-2 sm:mr-3 ring-2 ring-white shadow-sm flex-shrink-0"
                  style={{ backgroundColor: project.color }}
                />
                <span className={`task-title break-words ${
                  isSelected ? 'font-semibold' : ''
                }`} style={{ color: isSelected ? project.color : undefined }}>
                  {project.name}
                </span>
              </div>
            </button>
          </div>

          {/* 2行目: チームバッジ + アクションボタン（チームプロジェクトの場合のみ表示） */}
          {project.team && (
            <div className="relative flex items-center bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-indigo-900/20 dark:to-purple-900/20 py-0.5 pb-1 px-2 sm:px-3 -mt-1">
              {/* チーム情報バッジ */}
              <div className="flex items-center gap-1 flex-shrink-0 ml-8 sm:ml-10 pr-24 sm:pr-32">
                {/* チーム名バッジ（固定幅） */}
                <Tooltip content={`チーム: ${project.team.name}`} position="top">
                  <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-medium w-[50px] sm:w-[60px] truncate block text-center">
                    {project.team.name}
                  </span>
                </Tooltip>
                {/* 権限バッジ */}
                {project.team_role && (
                  <Tooltip content={
                    project.team_role === 'owner' ? 'オーナー' :
                    project.team_role === 'admin' ? '管理者' :
                    'メンバー'
                  } position="top">
                    <span className={`text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      project.team_role === 'owner'
                        ? 'bg-purple-100 text-purple-700'
                        : project.team_role === 'admin'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {project.team_role === 'owner' ? '👑' : project.team_role === 'admin' ? '🛡️' : '👤'}
                    </span>
                  </Tooltip>
                )}
                {/* メンバー数バッジ（クリック可能） */}
                {project.team_member_count !== undefined && (
                  <Tooltip content="クリックでメンバー一覧を表示" position="top">
                    <span
                      ref={membersButtonRef}
                      onClick={(e) => {
                        e.stopPropagation()
                        if (!showMembersList && membersButtonRef.current) {
                          setMembersButtonRect(membersButtonRef.current.getBoundingClientRect())
                        }
                        setShowMembersList(!showMembersList)
                      }}
                      className="relative text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium cursor-pointer hover:bg-blue-200 transition-colors"
                    >
                      <Users className="h-2.5 w-2.5 sm:h-3 sm:w-3 inline mr-0.5" />
                      {project.team_member_count}
                    </span>
                  </Tooltip>
                )}
                {/* メンバーリストポップオーバー（ポータルで表示） */}
                {showMembersList && project.team_id && (
                  <TeamMembersPopover
                    teamId={project.team_id}
                    memberCount={project.team_member_count}
                    onClose={() => setShowMembersList(false)}
                    anchorRect={membersButtonRect}
                  />
                )}
              </div>

              {/* アクションボタン（絶対位置で右端に配置）- 目玉と鉛筆のみ */}
              {!isTemporary && (
                <div className="absolute right-1 sm:right-2 flex items-center gap-[5px]">
                  {/* 表示/非表示トグル（目玉アイコン） */}
                  <Tooltip content={(project.user_show_in_all_projects !== undefined ? project.user_show_in_all_projects : true) ? "全プロジェクトで表示中" : "全プロジェクトで非表示"} position="top">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        const currentValue = project.user_show_in_all_projects !== undefined ? project.user_show_in_all_projects : true
                        updateProject(project.id, { show_in_all_projects: !currentValue })
                      }}
                      className="p-0.5 hover:bg-gray-100 rounded transition-colors touch-manipulation"
                    >
                      {(project.user_show_in_all_projects !== undefined ? project.user_show_in_all_projects : true) ? (
                        <Eye className="h-5 w-5 sm:h-[22px] sm:w-[22px] text-gray-600" />
                      ) : (
                        <EyeOff className="h-5 w-5 sm:h-[22px] sm:w-[22px] text-gray-400" />
                      )}
                    </button>
                  </Tooltip>
                  {/* 設定モーダル（鉛筆アイコン） */}
                  <Tooltip content="プロジェクト設定" position="left">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        onOpenSettings()
                      }}
                      className="p-0.5 hover:bg-gray-100 rounded transition-colors touch-manipulation"
                    >
                      <Pencil className="h-5 w-5 sm:h-[22px] sm:w-[22px] text-gray-600" />
                    </button>
                  </Tooltip>
                </div>
              )}
            </div>
          )}

          {/* 個人プロジェクトの場合のアクションボタン（1行目の右側に配置）- 目玉と鉛筆のみ */}
          {!project.team && !isTemporary && (
            <div className="absolute top-1/2 -translate-y-1/2 right-0 flex items-center gap-[5px] pr-1 sm:pr-2" data-tour="project-buttons">
              {/* 表示/非表示トグル（目玉アイコン） */}
              <Tooltip content={project.show_in_all_projects ? "全プロジェクトで表示中" : "全プロジェクトで非表示"} position="top">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    updateProject(project.id, { show_in_all_projects: !project.show_in_all_projects })
                  }}
                  className="p-0.5 hover:bg-gray-100 rounded transition-colors touch-manipulation"
                  data-tour="project-visibility-toggle"
                >
                  {project.show_in_all_projects ? (
                    <Eye className="h-5 w-5 sm:h-[22px] sm:w-[22px] text-gray-600" />
                  ) : (
                    <EyeOff className="h-5 w-5 sm:h-[22px] sm:w-[22px] text-gray-400" />
                  )}
                </button>
              </Tooltip>
              {/* 設定モーダル（鉛筆アイコン） */}
              <Tooltip content="プロジェクト設定" position="left">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onOpenSettings()
                  }}
                  className="p-0.5 hover:bg-gray-100 rounded transition-colors touch-manipulation"
                  data-tour="project-settings"
                >
                  <Pencil className="h-5 w-5 sm:h-[22px] sm:w-[22px] text-gray-600" />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      )}

      {/* 削除確認ダイアログ */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="プロジェクトを削除"
        message={`「${project.name}」を削除しますか？\nこの操作は取り消せません。`}
        confirmText="削除"
        cancelText="キャンセル"
        onConfirm={() => {
          deleteProject(project.id)
          setShowDeleteConfirm(false)
        }}
        onCancel={() => setShowDeleteConfirm(false)}
        variant="danger"
      />
    </div>
  )
}

export default function ProjectList() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const {
    projects,
    selectedProjectId,
    setSelectedProjectId,
    addProject,
    updateProject,
    updateProjectOrder,
    deleteProject,
    isPersonalSectionVisible,
    isTeamSectionVisible,
    setIsPersonalSectionVisible,
    setIsTeamSectionVisible,
    syncGanttOrderFromTaskOrder,
    syncTaskOrderFromGanttOrder,
  } = useStore()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editProjectName, setEditProjectName] = useState('')
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [sharingProjectId, setSharingProjectId] = useState<string | null>(null)
  // プロジェクト設定モーダル
  const [settingsProjectId, setSettingsProjectId] = useState<string | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)  // 新規作成モーダル
  const [templateManagerOpen, setTemplateManagerOpen] = useState(false)
  // マイルストーン（DBから取得）
  const [localMilestones, setLocalMilestones] = useState<Milestone[]>([])
  const [localMilestoneTemplates, setLocalMilestoneTemplates] = useState<MilestoneTemplate[]>([])

  // プロジェクト設定モーダルが開いたときにDBからマイルストーンを取得
  useEffect(() => {
    if (!settingsProjectId) {
      setLocalMilestones([])
      return
    }

    const fetchMilestones = async () => {
      try {
        const dbMilestones = await getMilestonesByProjectId(settingsProjectId)
        setLocalMilestones(dbMilestones.map(convertDBMilestoneToLocal))
      } catch (error) {
        console.error('Failed to fetch milestones:', error)
      }
    }

    fetchMilestones()
  }, [settingsProjectId])

  // プロジェクト設定モーダルが開いたときにDBからテンプレートを取得
  useEffect(() => {
    const project = projects.find(p => p.id === settingsProjectId)
    const teamId = project?.team_id

    const fetchTemplates = async () => {
      try {
        if (teamId) {
          // チームプロジェクトの場合：チームテンプレートを取得
          // 初回はデフォルトテンプレートを初期化
          await initializeTeamTemplatesIfNeeded(teamId)
          const dbTemplates = await getMilestoneTemplatesByTeamId(teamId)
          setLocalMilestoneTemplates(dbTemplates.map(convertDBTemplateToLocal))
        } else if (user?.id) {
          // 個人プロジェクトの場合：個人テンプレートを取得
          // 初回はデフォルトテンプレートを初期化
          await initializePersonalTemplatesIfNeeded(user.id)
          const dbTemplates = await getMilestoneTemplatesByUserId(user.id)
          setLocalMilestoneTemplates(dbTemplates.map(convertDBTemplateToLocal))
        } else {
          setLocalMilestoneTemplates([])
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error)
      }
    }

    if (settingsProjectId) {
      fetchTemplates()
    } else {
      setLocalMilestoneTemplates([])
    }
  }, [settingsProjectId, projects, user?.id])

  // アコーディオン状態（PC用）
  const [isPersonalExpanded, setIsPersonalExpanded] = useState(true)
  const [isTeamExpanded, setIsTeamExpanded] = useState(true)
  // タスク順序同期の確認ダイアログ
  const [syncConfirmDialog, setSyncConfirmDialog] = useState<{
    isOpen: boolean
    title: string
    message: string
    onConfirm: () => void
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} })

  // プロジェクトを一時保存、チーム、個人に分離
  const temporaryProject = projects.find(p => p.name === '一時保存')
  const teamProjects = projects.filter(p => p.name !== '一時保存' && p.is_team_project && p.team_id)
  const personalProjects = projects.filter(p => p.name !== '一時保存' && !p.is_team_project)
  const regularProjects = [...personalProjects, ...teamProjects] // 並び替え用に結合

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10
      }
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,    // より長い遅延でタップとドラッグを区別
        tolerance: 8   // より小さい許容範囲でタッチ位置精度を向上
      }
    })
  )
  
  const handleUpdateProject = async (id: string) => {
    if (editProjectName.trim()) {
      await updateProject(id, { name: editProjectName.trim() })
      setEditingId(null)
      setEditProjectName('')
    }
  }

  const handleSelectAll = () => {
    setSelectedProjectId(null)
  }

  // プロジェクト設定モーダル用ハンドラ
  const settingsProject = projects.find(p => p.id === settingsProjectId) || null

  const handleAddMilestone = useCallback(async (milestone: Partial<Milestone>) => {
    if (!milestone.projectId) return
    try {
      const created = await createProjectMilestone({
        project_id: milestone.projectId,
        template_id: milestone.templateId || null,
        title: milestone.title || '',
        start_date: milestone.startDate || new Date().toISOString().split('T')[0],
        end_date: milestone.endDate || null,
        icon_type: milestone.iconType || 'emoji',
        icon_value: milestone.iconValue || '📋',
        color: milestone.color || '#6366f1',
        sort_order: milestone.sortOrder || localMilestones.length,
      })
      setLocalMilestones(prev => [...prev, convertDBMilestoneToLocal(created)])
    } catch (error) {
      console.error('Failed to create milestone:', error)
    }
  }, [localMilestones.length])

  const handleUpdateMilestone = useCallback(async (milestoneId: string, updates: Partial<Milestone>) => {
    try {
      const updated = await updateProjectMilestone(milestoneId, {
        template_id: updates.templateId,
        title: updates.title,
        start_date: updates.startDate,
        end_date: updates.endDate,
        icon_type: updates.iconType,
        icon_value: updates.iconValue,
        color: updates.color,
        sort_order: updates.sortOrder,
      })
      setLocalMilestones(prev =>
        prev.map(m => m.id === updated.id ? convertDBMilestoneToLocal(updated) : m)
      )
    } catch (error) {
      console.error('Failed to update milestone:', error)
    }
  }, [])

  const handleDeleteMilestone = useCallback(async (milestoneId: string) => {
    try {
      await deleteProjectMilestone(milestoneId)
      setLocalMilestones(prev => prev.filter(m => m.id !== milestoneId))
    } catch (error) {
      console.error('Failed to delete milestone:', error)
    }
  }, [])

  // 現在のプロジェクトのチームIDを取得するヘルパー
  const getCurrentTeamId = useCallback(() => {
    const project = projects.find(p => p.id === settingsProjectId)
    return project?.team_id || null
  }, [projects, settingsProjectId])

  const handleAddTemplate = useCallback(async (template: Partial<MilestoneTemplate>) => {
    const teamId = getCurrentTeamId()
    try {
      let created: DBMilestoneTemplate
      if (teamId) {
        // チームテンプレートを作成
        created = await createMilestoneTemplate({
          team_id: teamId,
          title: template.title || '',
          icon_type: template.iconType || 'emoji',
          icon_value: template.iconValue || '📋',
          color: template.color || '#6366f1',
          sort_order: template.sortOrder || localMilestoneTemplates.length,
        })
      } else if (user?.id) {
        // 個人テンプレートを作成
        created = await createPersonalMilestoneTemplate(user.id, {
          title: template.title || '',
          icon_type: template.iconType || 'emoji',
          icon_value: template.iconValue || '📋',
          color: template.color || '#6366f1',
          sort_order: template.sortOrder || localMilestoneTemplates.length,
        })
      } else {
        console.error('No team ID or user ID available for creating template')
        return
      }
      setLocalMilestoneTemplates(prev => [...prev, convertDBTemplateToLocal(created)])
    } catch (error) {
      console.error('Failed to create template:', error)
    }
  }, [getCurrentTeamId, localMilestoneTemplates.length, user?.id])

  const handleUpdateTemplate = useCallback(async (templateId: string, updates: Partial<MilestoneTemplate>) => {
    try {
      const updated = await updateMilestoneTemplate(templateId, {
        title: updates.title,
        icon_type: updates.iconType,
        icon_value: updates.iconValue,
        color: updates.color,
        sort_order: updates.sortOrder,
      })
      setLocalMilestoneTemplates(prev =>
        prev.map(t => t.id === updated.id ? convertDBTemplateToLocal(updated) : t)
      )
    } catch (error) {
      console.error('Failed to update template:', error)
    }
  }, [])

  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    try {
      await deleteMilestoneTemplate(templateId)
      setLocalMilestoneTemplates(prev => prev.filter(t => t.id !== templateId))
    } catch (error) {
      console.error('Failed to delete template:', error)
    }
  }, [])

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id) return

    // ドラッグ元のプロジェクトを特定
    const draggedProject = regularProjects.find(p => p.id === active.id)
    if (!draggedProject) return

    // 個人プロジェクトかチームプロジェクトかを判定
    const isPersonalProject = !draggedProject.is_team_project
    const targetProjects = isPersonalProject ? personalProjects : teamProjects

    const oldIndex = targetProjects.findIndex(p => p.id === active.id)
    const newIndex = targetProjects.findIndex(p => p.id === over.id)

    if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
      // display_orderの重複チェック
      const orderSet = new Set<number>()
      let hasDuplicates = false
      for (const project of targetProjects) {
        if (orderSet.has(project.display_order)) {
          hasDuplicates = true
          break
        }
        orderSet.add(project.display_order)
      }

      if (hasDuplicates) {
        // 重複がある場合: 全プロジェクトの順序を再割り当て
        const reorderedProjects = [...targetProjects]
        const [movedItem] = reorderedProjects.splice(oldIndex, 1)
        reorderedProjects.splice(newIndex, 0, movedItem)

        // 新しい順序を1000刻みで割り当て
        for (let i = 0; i < reorderedProjects.length; i++) {
          await updateProjectOrder(reorderedProjects[i].id, i * 1000)
        }
      } else {
        // 重複がない場合: Fractional Indexing
        let newOrder: number

        if (oldIndex < newIndex) {
          // 下に移動: over要素とその次の要素の間に配置
          const targetOrder = targetProjects[newIndex]?.display_order ?? 0
          const nextOrder = targetProjects[newIndex + 1]?.display_order ?? targetOrder + 2000
          newOrder = Math.floor((targetOrder + nextOrder) / 2)
        } else {
          // 上に移動: over要素の前の要素とover要素の間に配置
          const targetOrder = targetProjects[newIndex]?.display_order ?? 0
          const prevOrder = targetProjects[newIndex - 1]?.display_order ?? targetOrder - 2000
          newOrder = Math.floor((prevOrder + targetOrder) / 2)
        }

        await updateProjectOrder(active.id as string, newOrder)
      }
    }
  }

  const selectedProjectName = projects.find(p => p.id === selectedProjectId)?.name || t('project.allProjects')
  const selectedProjectColor = projects.find(p => p.id === selectedProjectId)?.color

  return (
    <div className="flex flex-col gap-1 sm:gap-3 overflow-x-hidden" data-tour="project-list">
    {/* 個人プロジェクトの枠 */}
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="p-1.5 sm:p-3 border-b">
        <div className="flex items-center justify-between mb-0 sm:mb-4">
          <div className="flex items-center gap-1 flex-1 min-w-0">
            <h2 className="text-xs sm:text-lg font-semibold text-gray-900">{t('project.title')}</h2>
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="p-0.5 hover:bg-gray-100 rounded transition-colors sm:hidden"
            >
              {isCollapsed ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />}
            </button>
            {isCollapsed && (
              <div className="sm:hidden flex items-center gap-1 text-[10px] truncate flex-1 min-w-0">
                {selectedProjectColor && (
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: selectedProjectColor }} />
                )}
                <span className="truncate" style={{ color: selectedProjectColor }}>
                  {selectedProjectName}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 sm:gap-2" data-tour="add-project">
            <Tooltip content="プロジェクトを追加">
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-1 px-2 py-1 sm:px-3 sm:py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-xs sm:text-sm font-medium"
              >
                <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">追加</span>
              </button>
            </Tooltip>
          </div>
        </div>

      </div>

      <div className={`p-1 sm:p-2 ${isCollapsed ? 'hidden sm:block' : 'block'}`}>
        {/* すべてのプロジェクト - 常に薄い色で表示 */}
        <button
          onClick={handleSelectAll}
          className={`w-full text-left px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg transition-colors flex items-center ${
            selectedProjectId === null
              ? 'bg-gradient-to-r from-pink-200/50 to-rose-200/50 text-gray-700 font-bold shadow-md border-2 border-pink-500'
              : 'bg-gradient-to-r from-pink-100/40 to-rose-100/40 text-gray-600 hover:from-pink-200/45 hover:to-rose-200/45 font-semibold shadow-sm border border-pink-400'
          }`}
        >
          <div
            className="w-3 h-3 sm:w-4 sm:h-4 rounded-full mr-2 sm:mr-3 ring-2 ring-white shadow-sm bg-white"
          />
          {t('project.allProjects')}
        </button>

        {/* 一時保存プロジェクト（固定位置・最上部） */}
        {temporaryProject && (
          <SortableProjectItem
            key={temporaryProject.id}
            project={temporaryProject}
            isSelected={selectedProjectId === temporaryProject.id}
            isEditing={editingId === temporaryProject.id}
            editProjectName={editProjectName}
            onSelect={() => setSelectedProjectId(temporaryProject.id)}
            onCancelEdit={() => {
              setEditingId(null)
              setEditProjectName('')
            }}
            onSaveEdit={() => handleUpdateProject(temporaryProject.id)}
            onOpenSettings={() => setSettingsProjectId(temporaryProject.id)}
            setEditProjectName={setEditProjectName}
            updateProject={updateProject}
            deleteProject={deleteProject}
            isTemporary={true}
          />
        )}

      </div>
    </div>

    {/* 個人プロジェクトの枠（チームプロジェクトと同様のスタイル） */}
    {personalProjects.length > 0 && (
      <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-lg shadow-sm border-2 border-gray-200">
        {/* アコーディオンヘッダー（クリック可能） */}
        <div className="hidden sm:flex w-full p-1.5 sm:p-4 border-b border-gray-200 bg-gradient-to-r from-gray-100/50 to-slate-100/50 rounded-t-lg items-center gap-2">
          <button
            onClick={() => setIsPersonalExpanded(!isPersonalExpanded)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {isPersonalExpanded ? (
              <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
            ) : (
              <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
            )}
            <h2 className="text-xs sm:text-lg font-bold text-gray-700">個人プロジェクト</h2>
            <span className="text-[10px] sm:text-xs text-gray-500 font-medium px-2 py-0.5 bg-gray-100 rounded-full">
              {personalProjects.length}件
            </span>
          </button>
          {/* セクション表示/非表示トグル（「全プロジェクト」選択時用） */}
          <div className="ml-auto">
            <Tooltip content={isPersonalSectionVisible ? "「全プロジェクト」表示時に非表示にする" : "「全プロジェクト」表示時に表示する"} position="left">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsPersonalSectionVisible(!isPersonalSectionVisible)
                }}
                className={`p-1 rounded transition-colors ${
                  isPersonalSectionVisible
                    ? 'text-gray-600 hover:bg-gray-200'
                    : 'text-gray-400 hover:bg-gray-200'
                }`}
              >
                {isPersonalSectionVisible ? (
                  <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </button>
            </Tooltip>
          </div>
        </div>
        {/* モバイル用ヘッダー（クリック不可） */}
        <div className="sm:hidden p-1.5 border-b border-gray-200 bg-gradient-to-r from-gray-100/50 to-slate-100/50 flex items-center gap-2">
          <h2 className="text-xs font-bold text-gray-700">個人プロジェクト</h2>
          <span className="text-[10px] text-gray-500 font-medium px-2 py-0.5 bg-gray-100 rounded-full">
            {personalProjects.length}件
          </span>
        </div>

        {/* プロジェクトリスト（モバイル: 常に表示、PC: アコーディオン開閉） */}
        <div className={`p-1 sm:p-2 ${isCollapsed ? 'hidden sm:block' : 'block'} ${!isPersonalExpanded ? 'sm:hidden' : ''}`}>
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={personalProjects.map(p => p.id)}
              strategy={verticalListSortingStrategy}
            >
              {personalProjects.map((project) => (
                <SortableProjectItem
                  key={project.id}
                  project={project}
                  isSelected={selectedProjectId === project.id}
                  isEditing={editingId === project.id}
                  editProjectName={editProjectName}
                  onSelect={() => setSelectedProjectId(project.id)}
                  onCancelEdit={() => {
                    setEditingId(null)
                    setEditProjectName('')
                  }}
                  onSaveEdit={() => handleUpdateProject(project.id)}
                  onOpenSettings={() => setSettingsProjectId(project.id)}
                  setEditProjectName={setEditProjectName}
                  updateProject={updateProject}
                  deleteProject={deleteProject}
                  isTemporary={false}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    )}

    {/* チームプロジェクトの枠（完全に独立） */}
    {teamProjects.length > 0 && (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-sm border-2 border-blue-200">
        {/* アコーディオンヘッダー（クリック可能） */}
        <div className="hidden sm:flex w-full p-1.5 sm:p-4 border-b border-blue-200 bg-gradient-to-r from-blue-100/50 to-indigo-100/50 rounded-t-lg items-center gap-2">
          <button
            onClick={() => setIsTeamExpanded(!isTeamExpanded)}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {isTeamExpanded ? (
              <ChevronUp className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            ) : (
              <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            )}
            <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            <h2 className="text-xs sm:text-lg font-bold text-blue-900">{TEAMS_ENABLED ? 'チームプロジェクト' : '共有プロジェクト'}</h2>
            <span className="text-[10px] sm:text-xs text-blue-600 font-medium px-2 py-0.5 bg-blue-100 rounded-full">
              {teamProjects.length}件
            </span>
          </button>
          {/* セクション表示/非表示トグル（「全プロジェクト」選択時用） */}
          <div className="ml-auto">
            <Tooltip content={isTeamSectionVisible ? "「全プロジェクト」表示時に非表示にする" : "「全プロジェクト」表示時に表示する"} position="left">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsTeamSectionVisible(!isTeamSectionVisible)
                }}
                className={`p-1 rounded transition-colors ${
                  isTeamSectionVisible
                    ? 'text-blue-600 hover:bg-blue-200'
                    : 'text-blue-400 hover:bg-blue-200'
                }`}
              >
                {isTeamSectionVisible ? (
                  <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                ) : (
                  <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                )}
              </button>
            </Tooltip>
          </div>
        </div>
        {/* モバイル用ヘッダー（クリック不可） */}
        <div className="sm:hidden p-1.5 border-b border-blue-200 bg-gradient-to-r from-blue-100/50 to-indigo-100/50 flex items-center gap-2">
          <Users className="h-4 w-4 text-blue-600" />
          <h2 className="text-xs font-bold text-blue-900">{TEAMS_ENABLED ? 'チームプロジェクト' : '共有プロジェクト'}</h2>
          <span className="text-[10px] text-blue-600 font-medium px-2 py-0.5 bg-blue-100 rounded-full">
            {teamProjects.length}件
          </span>
        </div>

        {/* プロジェクトリスト（モバイル: 常に表示、PC: アコーディオン開閉） */}
        <div className={`p-1 sm:p-2 ${isCollapsed ? 'hidden sm:block' : 'block'} ${!isTeamExpanded ? 'sm:hidden' : ''}`}>
          <DndContext
            sensors={sensors}
            collisionDetection={pointerWithin}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={teamProjects.map(p => p.id)}
              strategy={verticalListSortingStrategy}
            >
              {teamProjects.map((project) => (
                <SortableProjectItem
                  key={project.id}
                  project={project}
                  isSelected={selectedProjectId === project.id}
                  isEditing={editingId === project.id}
                  editProjectName={editProjectName}
                  onSelect={() => setSelectedProjectId(project.id)}
                  onCancelEdit={() => {
                    setEditingId(null)
                    setEditProjectName('')
                  }}
                  onSaveEdit={() => handleUpdateProject(project.id)}
                  onOpenSettings={() => setSettingsProjectId(project.id)}
                  setEditProjectName={setEditProjectName}
                  updateProject={updateProject}
                  deleteProject={deleteProject}
                  isTemporary={false}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    )}

    {/* プロジェクト共有モーダル */}
    {sharingProjectId && (
      <ShareProjectModal
        isOpen={true}
        onClose={() => setSharingProjectId(null)}
        projectId={sharingProjectId}
        projectName={projects.find(p => p.id === sharingProjectId)?.name || ''}
        currentTeamId={projects.find(p => p.id === sharingProjectId)?.team_id}
        onSuccess={async () => {
          // プロジェクト共有後、プロジェクト一覧とタスク一覧を再読み込み
          await useStore.getState().fetchProjects()
          // 現在選択中のプロジェクトのタスクも再読み込み（担当者・進捗クリアを反映）
          const { fetchTodos, selectedProjectId } = useStore.getState()
          await fetchTodos(selectedProjectId ?? undefined)
          setSharingProjectId(null)
        }}
      />
    )}

    {/* プロジェクト新規作成モーダル */}
    <ProjectSettingsModal
      isOpen={isCreateModalOpen}
      onClose={() => setIsCreateModalOpen(false)}
      project={null}
      milestones={[]}
      templates={localMilestoneTemplates}
      onUpdateProject={() => {}}
      onDeleteProject={() => {}}
      onAddMilestone={() => {}}
      onUpdateMilestone={() => {}}
      onDeleteMilestone={() => {}}
      onOpenTemplateManager={() => {}}
      onSyncTaskToGantt={() => {}}
      onSyncGanttToTask={() => {}}
      onCreateProject={async (data) => {
        // 1. プロジェクト作成
        const newProject = await addProject({
          name: data.name,
          description: data.description || null,
          color: data.color,
          is_active: true,
          show_thumbnail: false,
          show_in_all_projects: true,
          is_team_project: !!data.teamId,
          team_id: data.teamId || null
        })

        // 2. マイルストーン作成（あれば）
        if (newProject && data.pendingMilestones.length > 0) {
          for (const milestone of data.pendingMilestones) {
            await createProjectMilestone({
              project_id: newProject.id,
              template_id: milestone.templateId || null,
              title: milestone.title || '',
              start_date: milestone.startDate || new Date().toISOString().split('T')[0],
              end_date: milestone.endDate || null,
              icon_type: milestone.iconType || 'emoji',
              icon_value: milestone.iconValue || '📋',
              color: milestone.color || '#6366f1',
              sort_order: milestone.sortOrder || 0,
            })
          }
        }

        setIsCreateModalOpen(false)
      }}
    />

    {/* プロジェクト設定モーダル（編集用） */}
    <ProjectSettingsModal
      isOpen={!!settingsProjectId}
      onClose={() => setSettingsProjectId(null)}
      project={settingsProject}
      milestones={localMilestones}
      templates={localMilestoneTemplates}
      onUpdateProject={(projectId, updates) => {
        updateProject(projectId, updates)
      }}
      onDeleteProject={(projectId) => {
        deleteProject(projectId)
        setSettingsProjectId(null)
      }}
      onAddMilestone={handleAddMilestone}
      onUpdateMilestone={handleUpdateMilestone}
      onDeleteMilestone={handleDeleteMilestone}
      onOpenTemplateManager={() => setTemplateManagerOpen(true)}
      onSyncTaskToGantt={(projectId) => {
        const project = projects.find(p => p.id === projectId)
        if (!project) return
        setSyncConfirmDialog({
          isOpen: true,
          title: 'タスク順序の同期',
          message: `「${project.name}」のタスク画面の順序をガントチャートに反映しますか？`,
          onConfirm: async () => {
            await syncGanttOrderFromTaskOrder(projectId)
            setSyncConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} })
          },
        })
      }}
      onSyncGanttToTask={(projectId) => {
        const project = projects.find(p => p.id === projectId)
        if (!project) return
        setSyncConfirmDialog({
          isOpen: true,
          title: 'タスク順序の同期',
          message: `「${project.name}」のガントチャートの順序をタスク画面に反映しますか？`,
          onConfirm: async () => {
            await syncTaskOrderFromGanttOrder(projectId)
            setSyncConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} })
          },
        })
      }}
      teamName={settingsProject?.team?.name}
      onTeamChange={async () => {
        // チーム変更後にプロジェクトとタスクを再取得
        await useStore.getState().fetchProjects()
        const { fetchTodos, selectedProjectId } = useStore.getState()
        await fetchTodos(selectedProjectId ?? undefined)
      }}
    />

    {/* マイルストーンテンプレート管理モーダル */}
    <MilestoneTemplateModal
      isOpen={templateManagerOpen}
      onClose={() => setTemplateManagerOpen(false)}
      templates={localMilestoneTemplates}
      teamName={settingsProject?.team?.name}
      isPersonal={!settingsProject?.team_id}
      onAdd={handleAddTemplate}
      onUpdate={handleUpdateTemplate}
      onDelete={handleDeleteTemplate}
    />

    {/* タスク順序同期の確認ダイアログ */}
    <ConfirmDialog
      isOpen={syncConfirmDialog.isOpen}
      title={syncConfirmDialog.title}
      message={syncConfirmDialog.message}
      onConfirm={syncConfirmDialog.onConfirm}
      onCancel={() => setSyncConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: () => {} })}
    />
    </div>
  )
}